import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import {
  Avatar,
  Card,
  Grid,
  Stack,
  Table,
  Rating,
  Button,
  Divider,
  Container,
  Typography,
  TableCell,
  TableRow,
  TableBody,
  TableContainer,
  LinearProgress,
} from '@mui/material';
// components
import Page from 'src/components/Page';
import Iconify from 'src/components/Iconify';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
// routes / utils
import { PATH_DASHBOARD } from 'src/routes/paths';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { fDateTimeSuffix } from 'src/utils/formatTime';
import { InstructorsModal } from './InstructorsModal';

// ----------------------------------------------------------------------
// Instructor detail / mini-report: courses taught + rating + student feedback.
// Assembled from existing endpoints (instructors list, courses list filtered by
// instructor, instructor-evaluations?instructorId) — no new backend needed.

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <Card sx={{ p: 3, height: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h3" sx={{ mt: 1 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      ) : null}
    </Card>
  );
}

export default function InstructorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [instructor, setInstructor] = useState<any>(null);
  const [courses, setCourses] = useState<any[]>([]);
  const [feedback, setFeedback] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // "Edit profile" opens the same form the Instructors list uses.
  const [editOpen, setEditOpen] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const [insRes, courseRes, evalRes] = await Promise.all([
        axiosInstance.get(APIS.INSTRUCTORS.LIST),
        axiosInstance.get(APIS.COURSES.LIST),
        axiosInstance.get(APIS.INSTRUCTOR_EVALUATIONS.LIST, {
          params: { instructorId: id, pageSize: 100 },
        }),
      ]);
      const insList = insRes.data?.data ?? insRes.data ?? [];
      const insArr = Array.isArray(insList) ? insList : insList?.items ?? [];
      setInstructor(insArr.find((x: any) => String(x._id) === String(id)) || null);

      const courseList = courseRes.data?.data ?? courseRes.data ?? [];
      const cArr = Array.isArray(courseList) ? courseList : courseList?.items ?? [];
      setCourses(
        cArr.filter((c: any) => String(c.instructor?._id || c.instructor) === String(id))
      );

      const evalData = evalRes.data?.data ?? evalRes.data;
      setFeedback(Array.isArray(evalData?.items) ? evalData.items : []);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to load instructor', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const ratingCount = feedback.length;
  const avgRating = ratingCount
    ? feedback.reduce((s, f) => s + (Number(f.rating) || 0), 0) / ratingCount
    : 0;
  const name = instructor?.name || '—';

  return (
    <Page title="Instructor">
      <Container maxWidth="lg">
        <HeaderBreadcrumbs
          heading={name !== '—' ? `Instructor — ${name}` : 'Instructor'}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Instructors', href: PATH_DASHBOARD.instructors },
            { name },
          ]}
        />

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={instructor?.imageUrl || instructor?.image} alt={name} sx={{ width: 64, height: 64 }} />
            <Stack sx={{ flexGrow: 1 }}>
              <Typography variant="h5">{name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {instructor?.email || ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {instructor?.phone || ''}
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:edit-fill" />}
              disabled={!instructor}
              onClick={() => setEditOpen(true)}
            >
              Edit profile
            </Button>
          </Stack>
        </Card>

        {instructor && (
          <InstructorsModal
            open={editOpen}
            isEdit
            currentInstructor={instructor}
            handleClose={() => setEditOpen(false)}
            getInstructorsList={loadDetail}
          />
        )}

        <Grid container spacing={3} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Avg rating"
              value={ratingCount ? `${avgRating.toFixed(1)}/5` : '—'}
              sub={`${ratingCount} ratings`}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard label="Courses" value={courses.length} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard label="Ratings" value={ratingCount} />
          </Grid>
        </Grid>

        {/* Courses taught */}
        <Card sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Courses ({courses.length})
            </Typography>
          </Stack>
          <Divider />
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableBody>
                {courses.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" sx={{ py: 3 }} colSpan={2}>
                      No courses assigned.
                    </TableCell>
                  </TableRow>
                ) : (
                  courses.map((c) => (
                    <TableRow key={c._id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">{c.title || c.name || '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          endIcon={<Iconify icon="eva:bar-chart-2-outline" />}
                          onClick={() => navigate(`${PATH_DASHBOARD.courses.view(c._id)}/report`)}
                        >
                          Report
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Student feedback (this instructor's ratings + comments) */}
        <Card>
          <Stack direction="row" alignItems="center" sx={{ p: 2, gap: 1 }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Student feedback ({ratingCount})
            </Typography>
            {ratingCount ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Rating value={avgRating} precision={0.1} size="small" readOnly />
                <Typography variant="body2">{avgRating.toFixed(1)}/5</Typography>
              </Stack>
            ) : null}
          </Stack>
          <Divider />
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableBody>
                {ratingCount === 0 ? (
                  <TableRow>
                    <TableCell align="center" sx={{ py: 3 }} colSpan={3}>
                      No ratings yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  feedback.map((f) => (
                    <TableRow key={f._id} hover>
                      <TableCell sx={{ width: 240 }}>
                        <Stack spacing={0.25}>
                          <Typography variant="subtitle2" noWrap>
                            {f.userId?.name || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {f.userId?.email || ''}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ width: 140 }}>
                        <Rating value={Number(f.rating) || 0} size="small" readOnly />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{f.comment || '—'}</Typography>
                        {f.updatedAt || f.createdAt ? (
                          <Typography variant="caption" color="text.secondary">
                            {fDateTimeSuffix(new Date(f.updatedAt || f.createdAt))}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Page>
  );
}
